import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import cn from 'classnames';
import { getQueryString, useRouter } from '@/Framework/Router';
import { getBreadcrumbs, getLeagueTableItemRoute } from './helpers';
import { TABS, TOP_COMPANIES_TABLE_COLUMNS } from './constants';

import PageHeaderWrapper from '@/finsight/ui/common/components/PageHeaderWrapper';
import MissingSomethingModalButton from '@/finsight/ui/common/components/MissingSomethingModalButton';

import SearchInput from './LeagueSearchInput';
import TTMSelect from './TTMSelect';

import TopUnderwriters from './Underwriters/TopUnderwriters';
import TopUnderwritersTTMSelect from './Underwriters/TopUnderwriters/TTMSelect';

import TopRatingAgencies from './RatingAgencies/TopRatingAgencies';
import TopRatingAgenciesTTMSelect from './RatingAgencies/TopRatingAgencies/TTMSelect';

import MetaSchema from '@/finsight/ui/common/components/MetaSchema/MetaSchema';
import { useBreadcrumbsContext } from '@/finsight/ui/common/components/header/Breadcrumbs/BreadcrumbsContext';
import { useLeagueTableContext } from '@/finsight/application/League/Table/LeagueTableContext';
import TogglingFilters from '@/finsight/ui/components/profile/TogglingFilters';

import { setMetaTags } from '@/finsight/application/actions/metaTags/actions';
import metaTags from '@/finsight/ui/common/metaTagsConstants';
import { initProductRegionFilters } from '@/finsight/application/actions/pageFilters/productRegionFilters/actions';
import { resetPageFilters } from '@/finsight/application/actions/pageFilters/actions';
import LeagueTableTab from '@/finsight/ui/components/league/LeagueTablePage/LeagueTableTab';
import LeagueTableTabContent from '@/finsight/ui/components/league/LeagueTablePage/LeagueTableTabContent';
import { useLeagueTableFiltersContext } from '@/finsight/application/League/Table/LeagueTableFiltersContext';
import { useDictionariesContext } from '@/finsight/application/Dictionaries/DictionariesContext';
import CheckUserAccess from 'src/finsight/ui/common/components/CheckUserAccess';
import { UserPlanType } from '@/users/domain/UserPlan';
import { LeagueTablesNoAccessComponent } from '@/finsight/ui/components/league/LeagueTablePage/LeagueTablesNoAccess';
import { useUserPlanContext } from '@/finsight/application/User/UserPlan/UserPlanContext';
import { usePageFiltersContext } from '@/finsight/application/PageFilters/PageFiltersContext';

import styles from './leagueTablePage.scss';
import cardStyles from '@dealroadshow/uikit/core/styles/card.scss';
import spacesStyles from '@dealroadshow/uikit/core/styles/helpers/spaces.scss';
import tabsStyles from '@dealroadshow/uikit/core/styles/tabs.scss';

const LeagueTablePage = () => {
  const dispatch = useDispatch();
  const { query } = useRouter();
  const { products, regions, q } = query;
  const search = { products, regions, q };
  const didMount = useRef(false);
  const { setBreadcrumbs } = useBreadcrumbsContext();
  const {
    state: leagueTableState,
    getCompaniesAllByFilter,
    getRatingAgenciesByFilter,
    getUnderwritersByFilter,
    paginateCompaniesAll,
    paginateRatingAgencies,
    paginateUnderwriters,
    resetCompaniesAll,
    resetUnderwriters,
    resetRatingAgencies,
  } = useLeagueTableContext();
  const { state: filtersState } = useLeagueTableFiltersContext();
  const [initialProductsRegionsFilters, setInitialProductsRegionsFilters] = useState({});
  const isPageLoading = leagueTableState.underwritersCollection.isFetching
    || leagueTableState.topUnderwritersCollection.isFetching
    || leagueTableState.ratingAgenciesCollection.isFetching
    || leagueTableState.topRatingAgenciesCollection.isFetching
    || leagueTableState.companiesAllCollection.isFetching;

  const { checkAccessByUserPlan } = useUserPlanContext();
  const { checkAccessByUserRegions, checkAccessByUserProducts } = usePageFiltersContext();
  const checks = [
    checkAccessByUserPlan(UserPlanType.PREMIUM),
    checkAccessByUserRegions(),
    checkAccessByUserProducts(),
  ];

  const selectedPeriod = filtersState.periods.leagueTable.selected;

  const [isPagesReset, setIsPagesReset] = useState(false);

  const {
    isHeaderInitialized,
    productsList,
    regionsList,
  } = useDictionariesContext();

  useEffect(() => {
    if (isHeaderInitialized) {
      dispatch(setMetaTags(metaTags.leagueTable()));
    }
  }, [isHeaderInitialized]);

  useEffect(() => {
    if (isHeaderInitialized) {
      dispatch(initProductRegionFilters(
        productsList,
        regionsList,
        true,
      ));
    }
  }, [products, regions, isHeaderInitialized]);

  useEffect(() => {
    setInitialProductsRegionsFilters(search);

    return () => {
      resetCompaniesAll();
      resetUnderwriters();
      resetRatingAgencies();
      dispatch(resetPageFilters());
    };
  }, []);

  useEffect(() => {
    if (didMount.current) {
      paginateCompaniesAll(1);
      paginateRatingAgencies(1);
      paginateUnderwriters(1);
      setIsPagesReset(true);
    } else {
      didMount.current = true;
    }
  }, [q, selectedPeriod, products, regions]);

  useEffect(() => {
    if (isPagesReset) {
      getCompaniesAllByFilter();
      getRatingAgenciesByFilter();
      getUnderwritersByFilter();
      setIsPagesReset(false);
    }
  }, [isPagesReset]);

  useEffect(() => {
    setBreadcrumbs(getBreadcrumbs(initialProductsRegionsFilters));
    return () => setBreadcrumbs(null);
  }, []);

  const cardContainerCls = cn(cardStyles.cardContainer, spacesStyles.mbn, styles.cardContainer);
  const cardInnerCls = cn(cardStyles.cardInner, spacesStyles.pbn, styles.cardInner);
  const topCompaniesTablesCommonProps = {
    rowClassName: styles.tableRow,
    containerClassName: styles.topItemsTableContainer,
    tableClassName: styles.topItemsTable,
    emptyMessageClassName: styles.topItemsEmptyMessage,
    columns: TOP_COMPANIES_TABLE_COLUMNS,
    getRoutePath: getLeagueTableItemRoute(getQueryString(search)),
    showColumnsHeaders: false,
    isItemsPerPageVisible: false,
  };

  const renderContent = () => (
    <>
      <div className={ styles.row }>
        <TopUnderwriters
          title="Top Underwriters"
          headerActionsComponent={ TopUnderwritersTTMSelect }
          { ...topCompaniesTablesCommonProps }
        />
        <TopRatingAgencies
          title="Top Rating Agencies"
          headerActionsComponent={ TopRatingAgenciesTTMSelect }
          { ...topCompaniesTablesCommonProps }
        />
      </div>
      <div className={ cardContainerCls }>
        <div className={ cardInnerCls }>
          <div className={ styles.tabsContainer } data-test="leagueTableTabs">
            { Object.values(TABS).map((tabItem) => (
              <LeagueTableTab key={ tabItem.path } { ...tabItem } />
            )) }
          </div>
          <div className={ styles.cardHeader }>
            <TTMSelect />
            <SearchInput />
          </div>
        </div>
        <div className={ cn(tabsStyles.tabsFooter, styles.tabsFooter) } />
        <LeagueTableTabContent />
      </div>
    </>
  );

  return (
    <>
      <MetaSchema />
      <PageHeaderWrapper
        title="Fixed Income Underwriter & Rating Agency League Tables"
        userAccessChecks={ checks }
      >
        <CheckUserAccess
          checks={ checks }
          fallback={ null }
        >
          <TogglingFilters isDisabled={ isPageLoading } />
        </CheckUserAccess>
      </PageHeaderWrapper>
      <CheckUserAccess
        checks={ checks }
        fallback={ LeagueTablesNoAccessComponent }
      >
        { renderContent() }
      </CheckUserAccess>
      <MissingSomethingModalButton className={ cn(spacesStyles.ptxl, spacesStyles.pbxl) } />
    </>
  );
};

export default LeagueTablePage;
